import { useEffect, useMemo, useState } from "react"
import { Plus, Trash2, Wand2, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { CopyButton } from "@f2e/ui/components/Button"
import {
  Badge,
  Button,
  Card,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@f2e/ui/components/base"

import {
  Agent,
  ClientChannel,
  WidgetBaseColor,
  WidgetClientConfig,
  WidgetLocale,
  Workspace,
} from "@/types"
import { useToast } from "@/hooks"
import {
  FetchStatus,
  agentResponseToAgent,
  clientChannelResponseToClientChannel,
  makeUpdateClientChannelRequest,
  useGenerateWidgetPrivateAuthSecretApi,
  useGetPublishedAgentsApi,
  useRevokeWidgetPrivateAuthSecretApi,
  useUpdateClientChannelApi,
  useUploadClientChannelImageApi,
} from "@/hooks/api"

import {
  CLIENT_CHANNEL_IMAGE_ACCEPT,
  MAX_CLIENT_CHANNEL_IMAGE_SIZE_MB,
  isClientChannelImageTooLarge,
  isValidClientChannelImageType,
} from "./channelImageUpload"
import { WarningMessageBlock } from "./WarningMessageBlock"
import {
  getUserFacingApiErrorMessage,
  getUserFacingApiErrorToastProps,
} from "@/lib/apiErrors"

export interface WidgetConfigFieldsProps {
  config: WidgetClientConfig
  isEditing: boolean
  workspace: Workspace
  channel: ClientChannel
  onConfigChange?: (config: Partial<WidgetClientConfig>) => void
  onChannelUpdated?: (channel: ClientChannel) => void
}

const DEFAULT_WIDGET_BASE_COLOR: WidgetBaseColor = "default"
const MASKED_SECRET = "************"
const WIDGET_BASE_COLOR_OPTIONS: WidgetBaseColor[] = [
  "default",
  "blue",
  "green",
  "orange",
  "red",
  "rose",
  "violet",
  "yellow",
]
const WIDGET_BASE_COLOR_SWATCHES: Record<WidgetBaseColor, string> = {
  default: "#171717",
  blue: "#2563eb",
  green: "#16a34a",
  orange: "#ea580c",
  red: "#dc2626",
  rose: "#f43f5e",
  violet: "#7c3aed",
  yellow: "#eab308",
}
const WIDGET_LOCALE_OPTIONS: WidgetLocale[] = ["en", "zh-TW", "ja"]
type WidgetLoginVerificationSelectValue =
  | "disabled"
  | WidgetClientConfig["loginVerificationMode"]

export const WidgetConfigFields = ({
  config,
  isEditing,
  workspace,
  channel,
  onConfigChange,
  onChannelUpdated,
}: WidgetConfigFieldsProps) => {
  const { t } = useTranslation()
  const { successToast, alertToast } = useToast()
  const [agents, setAgents] = useState<Agent[]>([])
  const [issuer, setIssuer] = useState(channel.widgetPrivateAuth?.issuer || "")
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null)
  const [newAllowedOrigin, setNewAllowedOrigin] = useState("")

  const { data: agentsRespData, fetchFn: getAgents } =
    useGetPublishedAgentsApi()
  const {
    data: updateAuthRespData,
    error: updateAuthRespError,
    status: updateAuthStatus,
    fetchFn: updateClientChannel,
  } = useUpdateClientChannelApi(
    workspace.organizationId || "",
    workspace.id,
    channel.id
  )
  const {
    error: generateSecretRespError,
    status: generateSecretStatus,
    fetchFn: generateSecret,
  } = useGenerateWidgetPrivateAuthSecretApi(
    workspace.organizationId || "",
    workspace.id,
    channel.id
  )
  const {
    data: revokeSecretRespData,
    error: revokeSecretRespError,
    status: revokeSecretStatus,
    fetchFn: revokeSecret,
  } = useRevokeWidgetPrivateAuthSecretApi(
    workspace.organizationId || "",
    workspace.id,
    channel.id
  )
  const { status: uploadImageStatus, fetchFn: uploadImage } =
    useUploadClientChannelImageApi(
      workspace.organizationId || "",
      workspace.id,
      channel.id
    )

  useEffect(() => {
    setIssuer(channel.widgetPrivateAuth?.issuer || "")
  }, [channel.id, channel.widgetPrivateAuth?.issuer])

  useEffect(() => {
    setRevealedSecret(null)
  }, [channel.id])

  useEffect(() => {
    if (channel.widgetPrivateAuth?.hasSharedSecret) return
    setRevealedSecret(null)
  }, [channel.id, channel.widgetPrivateAuth?.hasSharedSecret])

  useEffect(() => {
    getAgents({
      searchParams: new URLSearchParams({
        wid: workspace.id,
        oid: workspace.organizationId || "",
      }),
    })
  }, [getAgents, workspace.id, workspace.organizationId])

  useEffect(() => {
    if (!agentsRespData?.data) return
    setAgents(agentsRespData.data.map(agentResponseToAgent))
  }, [agentsRespData])

  useEffect(() => {
    if (!updateAuthRespData?.data) return
    onChannelUpdated?.(
      clientChannelResponseToClientChannel(updateAuthRespData.data)
    )
    successToast({
      title: t("workspace.channels.config.widget.privateAuth.updated.title"),
      description: t(
        "workspace.channels.config.widget.privateAuth.updated.description"
      ),
    })
  }, [onChannelUpdated, successToast, t, updateAuthRespData])

  useEffect(() => {
    if (!updateAuthRespError) return
    alertToast({
      title: t("workspace.channels.config.widget.privateAuth.error.title"),
      description: getUserFacingApiErrorMessage(
        updateAuthRespError,
        t,
        t("workspace.channels.config.widget.privateAuth.error.description")
      ),
    })
  }, [alertToast, t, updateAuthRespError])

  useEffect(() => {
    if (!generateSecretRespError) return
    alertToast({
      title: t("workspace.channels.config.widget.privateAuth.error.title"),
      description: getUserFacingApiErrorMessage(
        generateSecretRespError,
        t,
        t("workspace.channels.config.widget.privateAuth.error.description")
      ),
    })
  }, [alertToast, generateSecretRespError, t])

  useEffect(() => {
    if (!revokeSecretRespData?.data) return
    setRevealedSecret(null)
    onChannelUpdated?.(
      clientChannelResponseToClientChannel(revokeSecretRespData.data)
    )
    successToast({
      title: t(
        "workspace.channels.config.widget.privateAuth.secretRevoked.title"
      ),
      description: t(
        "workspace.channels.config.widget.privateAuth.secretRevoked.description"
      ),
    })
  }, [onChannelUpdated, revokeSecretRespData, successToast, t])

  useEffect(() => {
    if (!revokeSecretRespError) return
    alertToast({
      title: t("workspace.channels.config.widget.privateAuth.error.title"),
      description: getUserFacingApiErrorMessage(
        revokeSecretRespError,
        t,
        t("workspace.channels.config.widget.privateAuth.error.description")
      ),
    })
  }, [alertToast, revokeSecretRespError, t])

  const ui = config.ui
  const isUploadingLauncherIcon = uploadImageStatus === FetchStatus.Loading
  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === config.agentId),
    [agents, config.agentId]
  )
  const selectedAgentQuestions = useMemo(
    () =>
      (selectedAgent?.suggestedQuestions || [])
        .map((question) => question.trim())
        .filter(Boolean),
    [selectedAgent]
  )
  const hasConfiguredSecret =
    channel.widgetPrivateAuth?.hasSharedSecret || Boolean(revealedSecret)
  const trimmedAllowedOrigins = config.allowedOrigins
    .map((origin) => origin.trim())
    .filter(Boolean)
  const hasConfiguredOrigins = trimmedAllowedOrigins.length > 0
  const isDirectLoginMode =
    config.loginEnabled && config.loginVerificationMode === "direct_unverified"
  const shouldShowPrivateAuth =
    config.loginEnabled && config.loginVerificationMode === "assertion"
  const shouldShowAllowedOriginsEditor =
    config.originPolicyMode === "configured_origins_only"
  const shouldShowEmptyAllowedOriginsWarning =
    shouldShowAllowedOriginsEditor && !hasConfiguredOrigins
  const resolvedBaseColor = ui.theme.baseColor || DEFAULT_WIDGET_BASE_COLOR
  const resolvedLocale = ui.locale || "en"
  const resolvedLoginVerificationMode: WidgetLoginVerificationSelectValue =
    config.loginEnabled ? config.loginVerificationMode : "disabled"

  const handleSaveIssuer = () => {
    updateClientChannel({
      jsonBody: {
        ...makeUpdateClientChannelRequest(undefined, undefined, undefined, {
          issuer: issuer.trim() || null,
        }),
      },
    })
  }

  const handleGenerateSecret = () => {
    const nextIssuer = issuer.trim()

    generateSecret({
      callbackFn: (resp) => {
        if (!resp?.data) return

        setRevealedSecret(resp.data.shared_secret)
        onChannelUpdated?.({
          ...channel,
          widgetPrivateAuth: {
            issuer: nextIssuer,
            hasSharedSecret: true,
            secretLastRegeneratedAt: new Date(
              resp.data.secret_last_regenerated_at
            ),
          },
        })
        successToast({
          title: t(
            "workspace.channels.config.widget.privateAuth.secretGenerated.title"
          ),
          description: t(
            "workspace.channels.config.widget.privateAuth.secretGenerated.description"
          ),
        })
      },
    })
  }

  const handleLauncherIconUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!isValidClientChannelImageType(file)) {
      alertToast({
        title: t(
          "workspace.channels.config.widget.imageUpload.invalidFileType.title"
        ),
        description: t(
          "workspace.channels.config.widget.imageUpload.invalidFileType.description"
        ),
      })
      event.target.value = ""
      return
    }

    if (isClientChannelImageTooLarge(file)) {
      alertToast({
        title: t(
          "workspace.channels.config.widget.imageUpload.fileSizeTooLarge.title"
        ),
        description: t(
          "workspace.channels.config.widget.imageUpload.fileSizeTooLarge.description"
        ),
      })
      event.target.value = ""
      return
    }

    const formData = new FormData()
    formData.append("slot", "widget_launcher")
    formData.append("file", file)

    try {
      const resp = await uploadImage({ body: formData })
      const meta = resp?.data
      if (!meta?.url) {
        alertToast({
          title: t("workspace.channels.config.widget.imageUpload.error.title"),
          description: t(
            "workspace.channels.config.widget.imageUpload.error.description"
          ),
        })
        return
      }

      onConfigChange?.({
        ui: {
          ...ui,
          launcher: {
            ...ui.launcher,
            iconUrl: meta.url,
          },
        },
      })

      successToast({
        title: t("workspace.channels.config.widget.imageUpload.success.title"),
        description: t(
          "workspace.channels.config.widget.imageUpload.success.description"
        ),
      })
    } catch (error) {
      console.error("Failed to upload widget launcher image:", error)
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("workspace.channels.config.widget.imageUpload.error.title"),
          fallbackDescription: t(
            "workspace.channels.config.widget.imageUpload.error.description"
          ),
        }),
      })
    } finally {
      event.target.value = ""
    }
  }

  const handleBaseColorChange = (baseColor: WidgetBaseColor) => {
    onConfigChange?.({
      ui: {
        ...ui,
        theme: {
          ...ui.theme,
          baseColor,
        },
      },
    })
  }

  const handleAllowedOriginChange = (index: number, value: string) => {
    onConfigChange?.({
      allowedOrigins: config.allowedOrigins.map((origin, currentIndex) =>
        currentIndex === index ? value : origin
      ),
    })
  }

  const handleAddAllowedOrigin = () => {
    const nextOrigin = newAllowedOrigin.trim()
    if (!nextOrigin) return

    const existingOrigins = new Set(
      config.allowedOrigins.map((origin) => origin.trim()).filter(Boolean)
    )
    if (existingOrigins.has(nextOrigin)) {
      setNewAllowedOrigin("")
      return
    }

    onConfigChange?.({
      allowedOrigins: [...config.allowedOrigins, nextOrigin],
    })
    setNewAllowedOrigin("")
  }

  const handleRemoveAllowedOrigin = (index: number) => {
    onConfigChange?.({
      allowedOrigins: config.allowedOrigins.filter(
        (_, currentIndex) => currentIndex !== index
      ),
    })
  }

  const handleLoginVerificationModeChange = (
    value: WidgetLoginVerificationSelectValue
  ) => {
    if (value === "disabled") {
      onConfigChange?.({ loginEnabled: false })
      return
    }

    onConfigChange?.({
      loginEnabled: true,
      loginVerificationMode: value,
    })
  }

  return (
    <div className="space-y-3">
      <Card className="p-4 gap-4">
        <div className="space-y-1">
          <h4 className="py-2 font-semibold">
            {t("workspace.channels.config.widget.basicSetup.title")}
          </h4>
          <p className="pt-2 text-sm text-muted-foreground">
            {t("workspace.channels.config.widget.basicSetup.description")}
          </p>
        </div>

        {/* Agent Selection */}
        <div className="space-y-2 mb-2">
          <Label htmlFor="widget-config-agent">
            {t("workspace.channels.config.widget.agent")}
          </Label>
          <Select
            value={config.agentId || undefined}
            onValueChange={(value) => onConfigChange?.({ agentId: value })}
            disabled={!isEditing}
          >
            <SelectTrigger id="widget-config-agent">
              <SelectValue
                placeholder={t(
                  "workspace.channels.config.widget.agentPlaceholder"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="space-y-3 rounded-md border p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-1">
                <Label>
                  {t("workspace.channels.config.widget.suggestions")}
                </Label>
              </div>
            </div>

            {!config.agentId ? (
              <p className="text-sm text-muted-foreground">
                {t("workspace.channels.config.widget.suggestionsSelectAgent")}
              </p>
            ) : !selectedAgent ? (
              <p className="text-sm text-muted-foreground">
                {t("workspace.channels.config.widget.suggestionsUnavailable")}
              </p>
            ) : selectedAgentQuestions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("workspace.channels.config.widget.suggestionsEmpty")}
              </p>
            ) : (
              <div className="space-y-2">
                {selectedAgentQuestions.map((question, index) => (
                  <div
                    key={`${selectedAgent.id}-${index}`}
                    className="rounded-md bg-muted px-3 py-2 text-sm"
                  >
                    {question}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Widget Launcher Appearance */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="widget-config-launcher-label">
              {t("workspace.channels.config.widget.launcherLabel")}
            </Label>
            <Input
              id="widget-config-launcher-label"
              value={ui.launcher.label}
              disabled={!isEditing}
              onChange={(event) =>
                onConfigChange?.({
                  ui: {
                    ...ui,
                    launcher: {
                      ...ui.launcher,
                      label: event.target.value,
                    },
                  },
                })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="widget-config-launcher-icon">
              {t("workspace.channels.config.widget.launcherIcon")}
            </Label>
            {ui.launcher.iconUrl ? (
              <div className="flex items-center">
                <img
                  src={ui.launcher.iconUrl}
                  alt={t(
                    "workspace.channels.config.widget.launcherIconPreviewAlt"
                  )}
                  className="h-24 w-full bg-muted object-contain"
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("workspace.channels.config.widget.launcherIconPlaceholder")}
              </p>
            )}
            <Input
              id="widget-config-launcher-icon"
              type="file"
              accept={CLIENT_CHANNEL_IMAGE_ACCEPT}
              disabled={!isEditing || isUploadingLauncherIcon}
              onChange={handleLauncherIconUpload}
            />
            <p className="text-xs text-muted-foreground">
              {t(
                "workspace.channels.config.widget.imageUpload.constraints",
                `PNG or JPEG, max ${MAX_CLIENT_CHANNEL_IMAGE_SIZE_MB}MB`
              )}
            </p>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="widget-config-title">
              {t("workspace.channels.config.widget.title")}
            </Label>
            <Input
              id="widget-config-title"
              value={ui.title}
              disabled={!isEditing}
              onChange={(event) =>
                onConfigChange?.({
                  ui: {
                    ...ui,
                    title: event.target.value,
                  },
                })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="widget-config-locale">
              {t("workspace.channels.config.widget.locale")}
            </Label>
            <Select
              value={resolvedLocale}
              onValueChange={(value) =>
                onConfigChange?.({
                  ui: {
                    ...ui,
                    locale: value as WidgetLocale,
                  },
                })
              }
              disabled={!isEditing}
            >
              <SelectTrigger id="widget-config-locale">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WIDGET_LOCALE_OPTIONS.map((locale) => (
                  <SelectItem key={locale} value={locale}>
                    {t(
                      `workspace.channels.config.widget.localeOptions.${locale}`
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="widget-config-position">
              {t("workspace.channels.config.widget.position")}
            </Label>
            <Select
              value={ui.position}
              onValueChange={(value) =>
                onConfigChange?.({
                  ui: {
                    ...ui,
                    position: value as WidgetClientConfig["ui"]["position"],
                  },
                })
              }
              disabled={!isEditing}
            >
              <SelectTrigger id="widget-config-position">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bottom-right">
                  {t("workspace.channels.config.widget.positionBottomRight")}
                </SelectItem>
                <SelectItem value="bottom-left">
                  {t("workspace.channels.config.widget.positionBottomLeft")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Widget Panel Appearance */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="widget-config-greeting">
              {t("workspace.channels.config.widget.greeting")}
            </Label>
            <Input
              id="widget-config-greeting"
              value={ui.panel.greeting || ""}
              disabled={!isEditing}
              onChange={(event) =>
                onConfigChange?.({
                  ui: {
                    ...ui,
                    panel: {
                      ...ui.panel,
                      greeting: event.target.value,
                    },
                  },
                })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="widget-config-base-color">
              {t("workspace.channels.config.widget.baseColor")}
            </Label>
            <div className="flex items-center gap-3">
              <div
                aria-hidden="true"
                className="h-10 w-10 shrink-0 rounded-md border"
                style={{
                  backgroundColor:
                    WIDGET_BASE_COLOR_SWATCHES[resolvedBaseColor],
                }}
              />
              <Select
                value={resolvedBaseColor}
                onValueChange={(value) =>
                  handleBaseColorChange(value as WidgetBaseColor)
                }
                disabled={!isEditing}
              >
                <SelectTrigger id="widget-config-base-color" className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WIDGET_BASE_COLOR_OPTIONS.map((baseColor) => (
                    <SelectItem key={baseColor} value={baseColor}>
                      {t(
                        `workspace.channels.config.widget.baseColorOptions.${baseColor}`
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4 gap-4">
        <div className="space-y-1">
          <h4 className="py-2 font-semibold">
            {t("workspace.channels.config.widget.accessSecurity.title")}
          </h4>
          <p className="pt-2 text-sm text-muted-foreground">
            {t("workspace.channels.config.widget.accessSecurity.description")}
          </p>
        </div>

        <div className="space-y-2 rounded-md border p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="font-medium">
              {t("workspace.channels.config.widget.allowAnonymousAccess")}
            </div>
            <Switch
              checked={config.allowAnonymousAccess}
              disabled={!isEditing}
              onCheckedChange={(checked) =>
                onConfigChange?.({ allowAnonymousAccess: checked })
              }
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {t(
              "workspace.channels.config.widget.allowAnonymousAccessDescription"
            )}
          </div>
        </div>

        <div className="space-y-4 rounded-md border p-4">
          <div className="space-y-2">
            <h5 className="font-medium">
              {t("workspace.channels.config.widget.originRestrictions.title")}
            </h5>
            <p className="text-sm text-muted-foreground">
              {t(
                "workspace.channels.config.widget.originRestrictions.description"
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label>
              {t("workspace.channels.config.widget.originPolicyMode")}
            </Label>
            <Select
              value={config.originPolicyMode}
              onValueChange={(value) => {
                const nextMode = value as WidgetClientConfig["originPolicyMode"]
                onConfigChange?.({
                  originPolicyMode: nextMode,
                })
              }}
              disabled={!isEditing}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="disabled">
                  {t("workspace.channels.config.widget.originPolicyDisabled")}
                </SelectItem>
                <SelectItem value="configured_origins_only">
                  {t(
                    "workspace.channels.config.widget.originPolicyConfiguredOnly"
                  )}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {shouldShowAllowedOriginsEditor ? (
            <div className="space-y-2">
              <Label>
                {t("workspace.channels.config.widget.allowedOrigins")}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t(
                  "workspace.channels.config.widget.allowedOriginsDescription"
                )}
              </p>

              {shouldShowEmptyAllowedOriginsWarning ? (
                <WarningMessageBlock
                  title={t(
                    "workspace.channels.config.widget.allowedOriginsEmptyWarning.title"
                  )}
                  description={t(
                    "workspace.channels.config.widget.allowedOriginsEmptyWarning.description"
                  )}
                />
              ) : null}

              <div className="space-y-2">
                {config.allowedOrigins.map((origin, index) => (
                  <div
                    key={`allowed-origin-${index}`}
                    className="flex items-start gap-2"
                  >
                    <Input
                      value={origin}
                      disabled={!isEditing}
                      onChange={(event) =>
                        handleAllowedOriginChange(index, event.target.value)
                      }
                      placeholder={t(
                        "workspace.channels.config.widget.allowedOriginsInputPlaceholder"
                      )}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={!isEditing}
                      aria-label={t(
                        "workspace.channels.config.widget.allowedOriginsRemove"
                      )}
                      onClick={() => handleRemoveAllowedOrigin(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <div className="flex items-start gap-2">
                  <Input
                    id="widget-config-allowed-origins-add"
                    value={newAllowedOrigin}
                    disabled={!isEditing}
                    onChange={(event) =>
                      setNewAllowedOrigin(event.target.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return
                      event.preventDefault()
                      handleAddAllowedOrigin()
                    }}
                    placeholder={t(
                      "workspace.channels.config.widget.allowedOriginsInputPlaceholder"
                    )}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!isEditing || !newAllowedOrigin.trim()}
                    onClick={handleAddAllowedOrigin}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("workspace.channels.config.widget.allowedOriginsAdd")}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-4 rounded-md border p-4">
          <div className="space-y-1">
            <h5 className="font-medium">
              {t("workspace.channels.config.widget.authenticatedLogin.title")}
            </h5>
            <p className="text-sm text-muted-foreground">
              {t(
                "workspace.channels.config.widget.authenticatedLogin.description"
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="widget-login-verification-mode">
              {t("workspace.channels.config.widget.loginVerificationMode")}
            </Label>
            <Select
              value={resolvedLoginVerificationMode}
              onValueChange={(value) =>
                handleLoginVerificationModeChange(
                  value as WidgetLoginVerificationSelectValue
                )
              }
              disabled={!isEditing}
            >
              <SelectTrigger id="widget-login-verification-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="disabled">
                  {t(
                    "workspace.channels.config.widget.loginVerificationModeDisabled"
                  )}
                </SelectItem>
                <SelectItem value="assertion">
                  {t(
                    "workspace.channels.config.widget.loginVerificationModeAssertion"
                  )}
                </SelectItem>
                <SelectItem value="direct_unverified">
                  {t(
                    "workspace.channels.config.widget.loginVerificationModeDirect"
                  )}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {t(
                "workspace.channels.config.widget.loginVerificationModeDescription"
              )}
            </p>
          </div>

          {config.loginEnabled ? (
            <>
              {isDirectLoginMode ? (
                <WarningMessageBlock
                  title={t(
                    "workspace.channels.config.widget.directModeWarning.title"
                  )}
                  description={t(
                    "workspace.channels.config.widget.directModeWarning.description"
                  )}
                />
              ) : null}

              {shouldShowPrivateAuth ? (
                <div className="space-y-4 rounded-md border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <h5 className="font-medium">
                        {t(
                          "workspace.channels.config.widget.privateAuth.title"
                        )}
                      </h5>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "workspace.channels.config.widget.privateAuth.description"
                        )}
                      </p>
                    </div>
                    <Badge variant="secondary">
                      {channel.widgetPrivateAuth?.hasSharedSecret
                        ? t(
                            "workspace.channels.config.widget.privateAuth.secretConfigured"
                          )
                        : t(
                            "workspace.channels.config.widget.privateAuth.secretMissing"
                          )}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="widget-private-auth-issuer">
                      {t("workspace.channels.config.widget.privateAuth.issuer")}
                    </Label>
                    <div className="flex gap-3">
                      <Input
                        id="widget-private-auth-issuer"
                        value={issuer}
                        disabled={!isEditing}
                        onChange={(event) => setIssuer(event.target.value)}
                        placeholder={t(
                          "workspace.channels.config.widget.privateAuth.issuerPlaceholder"
                        )}
                      />
                      <Button
                        type="button"
                        onClick={handleSaveIssuer}
                        disabled={
                          !isEditing || updateAuthStatus === FetchStatus.Loading
                        }
                      >
                        {t(
                          "workspace.channels.config.widget.privateAuth.saveIssuer"
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Label
                      htmlFor="widget-secret-management"
                      className="w-full"
                    >
                      {t(
                        "workspace.channels.config.widget.privateAuth.revealTitle"
                      )}
                    </Label>
                    <div id="widget-secret-management" className="flex gap-3">
                      <Button
                        type="button"
                        onClick={handleGenerateSecret}
                        disabled={
                          !isEditing ||
                          generateSecretStatus === FetchStatus.Loading
                        }
                      >
                        <Wand2 className="mr-2 h-4 w-4" />
                        {channel.widgetPrivateAuth?.hasSharedSecret
                          ? t(
                              "workspace.channels.config.widget.privateAuth.regenerateSecret"
                            )
                          : t(
                              "workspace.channels.config.widget.privateAuth.generateSecret"
                            )}
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => revokeSecret()}
                        disabled={
                          !isEditing ||
                          revokeSecretStatus === FetchStatus.Loading ||
                          !channel.widgetPrivateAuth?.hasSharedSecret
                        }
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t(
                          "workspace.channels.config.widget.privateAuth.revokeSecret"
                        )}
                      </Button>
                    </div>

                    {hasConfiguredSecret ? (
                      <div
                        className={`space-y-2 rounded-md border p-4 ${
                          revealedSecret
                            ? "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
                            : "border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                        }`}
                      >
                        <p
                          className={`text-sm ${
                            revealedSecret
                              ? "text-amber-800 dark:text-amber-200"
                              : "text-slate-600 dark:text-slate-400"
                          }`}
                        >
                          {revealedSecret
                            ? t(
                                "workspace.channels.config.widget.privateAuth.revealDescription"
                              )
                            : t(
                                "workspace.channels.config.widget.privateAuth.maskedDescription"
                              )}
                        </p>
                        <code
                          className={`block break-all rounded border p-3 text-xs ${
                            revealedSecret
                              ? "border-amber-200 bg-amber-100/80 text-amber-950 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-50"
                              : "border-slate-200 bg-white text-slate-900 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
                          }`}
                        >
                          {revealedSecret ? (
                            <div className="flex items-center gap-2">
                              <span>{revealedSecret}</span>
                              <CopyButton content={revealedSecret} />
                            </div>
                          ) : (
                            <span>{MASKED_SECRET}</span>
                          )}
                        </code>
                        {channel.widgetPrivateAuth?.secretLastRegeneratedAt ? (
                          <p
                            className={`text-xs ${
                              revealedSecret
                                ? "text-amber-700 dark:text-amber-300"
                                : "text-slate-500 dark:text-slate-400"
                            }`}
                          >
                            {t(
                              "workspace.channels.config.widget.privateAuth.lastRegeneratedAt",
                              {
                                value:
                                  channel.widgetPrivateAuth.secretLastRegeneratedAt.toLocaleString(),
                              }
                            )}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </Card>
    </div>
  )
}

export default WidgetConfigFields
